import { useEffect, useRef, useState } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';

const PublicLayout = () => {
  const location = useLocation();
  const videoRef = useRef(null);
  const hasInitializedAutoOpen = useRef(false);
  const [isPageLoading, setPageLoading] = useState(true);
  const [isVideoModalOpen, setVideoModalOpen] = useState(false);
  const [isVideoMuted, setVideoMuted] = useState(true);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setPageLoading(false);
    }, 2000);

    return () => window.clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (hasInitializedAutoOpen.current) return;

    hasInitializedAutoOpen.current = true;
    if (location.pathname === '/') {
      setVideoModalOpen(true);
    }
  }, [location.pathname]);

  useEffect(() => {
    if (!isVideoModalOpen) return undefined;

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setVideoModalOpen(false);
      }
    };

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', handleEscape);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleEscape);
    };
  }, [isVideoModalOpen]);

  useEffect(() => {
    if (isVideoModalOpen) {
      setVideoMuted(true);
    }
  }, [isVideoModalOpen]);

  const openVideoModal = () => setVideoModalOpen(true);

  const closeVideoModal = () => {
    setVideoMuted(true);
    setVideoModalOpen(false);
  };

  const playVideoWithSound = async () => {
    const videoElement = videoRef.current;
    if (!videoElement) return;

    setVideoMuted(false);
    videoElement.muted = false;
    videoElement.volume = 1;

    try {
      await videoElement.play();
    } catch (error) {
      setVideoMuted(true);
      videoElement.muted = true;
    }
  };

  const homeHref = location.pathname === '/' ? '#top' : '/';
  const reviewsHref = location.pathname === '/' ? '#customer-reviews' : '/#customer-reviews';
  const isHomeActive = location.pathname === '/' && location.hash !== '#customer-reviews';
  const isReviewsActive = location.pathname === '/' && location.hash === '#customer-reviews';
  const isFreeTrialActive = location.pathname === '/step';
  const isPrivacyActive = location.pathname === '/privacy-policy';
  const isTermsActive = location.pathname === '/terms-and-conditions';
  const isBookkeeperActive = location.pathname === '/register/bookeeper';

  const getNavItemClass = (isActive) =>
    isActive
      ? 'rounded-full bg-amber-400 px-3 py-2 font-semibold text-slate-950 shadow-sm transition hover:bg-amber-300'
      : 'rounded-full px-3 py-2 text-slate-100 transition hover:bg-white/10 hover:text-white';

  return (
    <>
      {isPageLoading ? (
        <div className="fixed inset-0 z-[80] flex items-center justify-center overflow-hidden bg-gradient-to-br from-slate-950 via-blue-950 to-slate-900 px-6">
          <div className="absolute inset-0 opacity-30">
            <div className="absolute left-1/2 top-1/2 h-72 w-72 -translate-x-1/2 -translate-y-1/2 rounded-full bg-blue-500/30 blur-3xl" />
            <div className="absolute left-[30%] top-[35%] h-40 w-40 rounded-full bg-cyan-400/20 blur-3xl" />
            <div className="absolute right-[28%] top-[58%] h-44 w-44 rounded-full bg-amber-300/20 blur-3xl" />
          </div>

          <div className="relative w-full max-w-md rounded-3xl border border-white/10 bg-white/10 p-8 text-center shadow-2xl backdrop-blur-xl">
            <div className="mx-auto flex h-24 w-24 items-center justify-center rounded-3xl bg-white shadow-xl ring-1 ring-slate-200">
              <img
                src={process.env.PUBLIC_URL + '/logoIcon.png'}
                alt="Pentabooks"
                className="h-14 w-auto animate-pulse"
              />
            </div>

            <div className="mt-6">
              <p className="text-xs font-semibold uppercase tracking-[0.35em] text-blue-200">
                Pentabooks
              </p>
              <h2 className="mt-3 text-3xl font-semibold text-white">
                Preparing your workspace
              </h2>
              <p className="mt-3 text-sm text-slate-200">
                Loading smart bookkeeping tools, reports, and your business workflow experience.
              </p>
            </div>

            <div className="mt-6 flex items-center justify-center gap-3">
              <span className="h-3 w-3 animate-bounce rounded-full bg-blue-400 [animation-delay:-0.2s]" />
              <span className="h-3 w-3 animate-bounce rounded-full bg-cyan-300 [animation-delay:-0.1s]" />
              <span className="h-3 w-3 animate-bounce rounded-full bg-amber-300" />
            </div>
          </div>
        </div>
      ) : null}

      <header id="top" className="sticky top-0 z-20 border-b border-blue-900/40 bg-gradient-to-r from-slate-950 via-blue-950 to-slate-900 shadow-lg backdrop-blur">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-4 py-4 sm:px-6 lg:px-20">
          <a href={homeHref} className="flex items-center gap-3 rounded-full bg-white px-4 py-2 shadow-md ring-1 ring-slate-200">
            <img
              className="h-10 w-auto"
              src={process.env.PUBLIC_URL + '/logoIcon.png'}
              alt="Pentabooks"
            />
            <span className="text-base font-semibold text-slate-900">Pentabooks</span>
          </a>
          <nav className="flex items-center gap-2 text-sm font-medium sm:gap-3">
            <a href={homeHref} className={getNavItemClass(isHomeActive)}>Home</a>
            <a href={reviewsHref} className={getNavItemClass(isReviewsActive)}>Reviews</a>
            <button
              type="button"
              onClick={openVideoModal}
              className={getNavItemClass(false)}
            >
              Watch Demo
            </button>
            <Link to="/step" className={getNavItemClass(isFreeTrialActive)}>30-Day Free Trial</Link>
            <Link to="/privacy-policy" className={getNavItemClass(isPrivacyActive)}>Privacy Policy</Link>
            <Link to="/terms-and-conditions" className={getNavItemClass(isTermsActive)}>Terms & Conditions</Link>
            <Link
              to="/register/bookeeper"
              className={isBookkeeperActive
                ? 'rounded-full bg-amber-400 px-4 py-2 font-semibold text-slate-950 shadow-sm transition hover:bg-amber-300'
                : 'rounded-full bg-blue-500 px-4 py-2 text-white shadow-sm transition hover:bg-blue-400'}
            >
              Bookkeeper Sign Up
            </Link>
          </nav>
        </div>
      </header>

      <Outlet context={{ openVideoModal }} />

      {isVideoModalOpen ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/70 px-4 py-8 backdrop-blur-sm"
          onClick={closeVideoModal}
          role="dialog"
          aria-modal="true"
          aria-labelledby="video-modal-title"
        >
          <div
            className="relative w-full max-w-5xl overflow-hidden rounded-3xl bg-white shadow-2xl"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-4 border-b border-slate-200 bg-gradient-to-r from-slate-900 via-slate-800 to-blue-900 px-6 py-5 text-white">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.25em] text-blue-200">Product Walkthrough</p>
                <h3 id="video-modal-title" className="mt-2 text-2xl font-semibold">
                  See how Pentabooks works in real business workflows
                </h3>
                <p className="mt-2 max-w-2xl text-sm text-slate-200">
                  A quick overview of bookkeeping, transaction tracking, and reporting inside the Pentabooks platform.
                </p>
              </div>
              <button
                type="button"
                onClick={closeVideoModal}
                className="rounded-full border border-white/20 bg-white/10 px-3 py-2 text-sm font-semibold text-white transition hover:bg-white/20"
                aria-label="Close video modal"
              >
                Close
              </button>
            </div>
            <div className="bg-slate-950 p-3 sm:p-6">
              <div className="overflow-hidden rounded-2xl border border-slate-800 bg-black shadow-inner">
                <video
                  ref={videoRef}
                  className="aspect-video w-full bg-black"
                  controls
                  preload="metadata"
                  autoPlay
                  muted={isVideoMuted}
                  playsInline
                >
                  <source src={`${process.env.PUBLIC_URL}/pentabook.mp4`} type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
              </div>
              <div className="mt-4 flex flex-col gap-3 rounded-2xl border border-slate-800 bg-slate-900/80 px-4 py-4 text-white sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm font-semibold text-white">Audio is ready when you are</p>
                  <p className="mt-1 text-sm text-slate-300">
                    Click once to continue the demo with sound enabled.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={playVideoWithSound}
                  className="inline-flex items-center justify-center rounded-full bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-blue-700"
                >
                  {isVideoMuted ? 'Play with Sound' : 'Sound On'}
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
};

export default PublicLayout;
